import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ago, roleLabel } from '../lib/format'
import type { Fleet, ListResponse, Tenant, User } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, Toolbar, useSearch, type Column } from '../components/DataTable'
import { Button, Chip, Field, Modal, Panel, toast } from '../components/ui'

/** Roles a principal may grant, mirroring auth.CanGrantRole on the server. */
function grantableRoles(tier: number): string[] {
  const all = [
    'platform_owner',
    'platform_support',
    'reseller_admin',
    'reseller_support',
    'client_admin',
    'fleet_manager',
    'dispatcher',
    'viewer',
    'driver',
  ]
  const roleTier: Record<string, number> = {
    platform_owner: 1,
    platform_support: 1,
    reseller_admin: 2,
    reseller_support: 2,
    client_admin: 3,
    fleet_manager: 4,
    dispatcher: 4,
    viewer: 4,
    driver: 4,
  }
  // You may create users at your own tier or below, never above.
  return all.filter((r) => (roleTier[r] ?? 4) >= tier)
}

export function Users() {
  const { can, user: me } = useAuth()
  const qc = useQueryClient()
  const search = useSearch()
  const [offset, setOffset] = useState(0)
  const [editing, setEditing] = useState<User | null>(null)
  const [creating, setCreating] = useState(false)
  const limit = 50

  const list = useQuery({
    queryKey: ['users', search.debounced, offset],
    queryFn: () => api<ListResponse<User>>(`/users${qs({ q: search.debounced, limit, offset })}`),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('User deleted', 'ok')
      void qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'User',
      sort: 'name',
      render: (u) => (
        <div className="min-w-0">
          <div className="text-[var(--color-ink)] truncate">
            {u.fullName}
            {u.id === me?.id && <span className="text-[var(--color-ink-4)]"> (you)</span>}
          </div>
          <div className="text-[11px] text-[var(--color-ink-4)] truncate">{u.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sort: 'role',
      width: '150px',
      render: (u) => (
        <div>
          <div>{roleLabel(u.role)}</div>
          <div className="label text-[9px] opacity-70">Tier {u.tier ?? tierOf(u.role)}</div>
        </div>
      ),
    },
    {
      key: 'tenant',
      header: 'Account',
      render: (u) => u.tenantName ?? '—',
    },
    {
      key: 'scope',
      header: 'Fleet access',
      render: (u) =>
        u.fleetScope?.length ? (
          <Chip tone="accent">{u.fleetScope.length} fleet(s)</Chip>
        ) : (
          <span className="text-[var(--color-ink-4)]">All fleets</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '90px',
      render: (u) =>
        u.status === 'active' ? (
          <Chip tone="ok">Active</Chip>
        ) : u.status === 'invited' ? (
          <Chip tone="accent">Invited</Chip>
        ) : (
          <Chip tone="muted">Disabled</Chip>
        ),
    },
    {
      key: 'last',
      header: 'Last seen',
      numeric: true,
      sort: 'last',
      width: '120px',
      render: (u) => ago(u.lastLoginAt),
    },
    {
      key: 'actions',
      header: '',
      width: '70px',
      render: (u) =>
        can('user.manage') && (
          <div className="flex justify-end gap-1">
            <button className="btn btn-ghost btn-sm px-1.5" onClick={() => setEditing(u)} title="Edit">
              <Icon name="edit" size={13} />
            </button>
            {u.id !== me?.id && (
              <button
                className="btn btn-ghost btn-sm px-1.5"
                style={{ color: 'var(--text-critical)' }}
                title="Delete"
                onClick={() => confirm(`Delete ${u.email}? They lose access immediately.`) && remove.mutate(u.id)}
              >
                <Icon name="trash" size={13} />
              </button>
            )}
          </div>
        ),
    },
  ]

  return (
    <Page
      title="Users"
      subtitle="Who can sign in, and what they can do"
      actions={
        can('user.manage') && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={13} />
            Add user
          </Button>
        )
      }
    >
      <Toolbar search={search.value} onSearch={search.setValue} placeholder="Name or email…" />

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(u) => u.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{ title: 'No users', body: 'Add someone to give them access to this account.' }}
        />
      </Panel>

      {(creating || editing) && (
        <UserForm
          user={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['users'] })
          }}
        />
      )}
    </Page>
  )
}

function UserForm({
  user,
  onClose,
  onSaved,
}: {
  user: User | null
  onClose: () => void
  onSaved: () => void
}) {
  const { user: me } = useAuth()
  const [form, setForm] = useState({
    // A new user goes into the actor's own account unless another is chosen.
    tenantId: user?.tenantId ?? me?.tenantId ?? '',
    email: user?.email ?? '',
    fullName: user?.fullName ?? '',
    phone: user?.phone ?? '',
    role: user?.role ?? 'viewer',
    password: '',
    status: user?.status ?? 'active',
    fleetScope: new Set(user?.fleetScope ?? []),
  })

  // Every account the actor can see, so a platform owner can place a user
  // directly in a reseller or a client without logging in as it first.
  const accounts = useQuery({
    queryKey: ['tenants', 'subtree'],
    queryFn: () => api<ListResponse<Tenant>>(`/tenants${qs({ scope: 'subtree', limit: 500 })}`),
    enabled: !user,
  })
  const ownKind: Tenant['kind'] = me?.tier === 1 ? 'platform' : me?.tier === 2 ? 'reseller' : 'client'
  const accountOptions: { id: string; name: string; kind: Tenant['kind'] }[] = [
    ...(me ? [{ id: me.tenantId, name: `${me.tenantName} (yours)`, kind: ownKind }] : []),
    ...(accounts.data?.items ?? [])
      .filter((t) => t.id !== me?.tenantId)
      .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.name.localeCompare(b.name))
      .map((t) => ({ id: t.id, name: t.name, kind: t.kind })),
  ]
  const targetKind = accountOptions.find((a) => a.id === form.tenantId)?.kind ?? ownKind

  // Roles the actor may grant, narrowed to the ones that make sense in the
  // chosen account: platform roles live only in the platform account, and
  // so on down the tiers.
  const roles = grantableRoles(me?.tier ?? 4).filter((r) => ROLES_BY_KIND[targetKind].includes(r))
  useEffect(() => {
    if (roles.length && !roles.includes(form.role)) setForm((f) => ({ ...f, role: roles[0]! }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKind])

  // Fleet restrictions refer to the chosen account's fleets, not the actor's.
  const fleets = useQuery({
    queryKey: ['fleets', form.tenantId],
    queryFn: () => api<{ items: Fleet[] }>(`/fleets${qs({ tenantId: form.tenantId || undefined })}`),
  })

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        ...(user ? {} : { tenantId: form.tenantId || undefined }),
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        role: form.role,
        status: form.status,
        fleetScope: [...form.fleetScope],
      }
      if (form.password) body.password = form.password
      return user ? api(`/users/${user.id}`, { method: 'PATCH', body }) : api('/users', { body })
    },
    onSuccess: () => {
      toast(user ? 'User updated' : 'User created', 'ok')
      onSaved()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const passwordTooShort = form.password.length > 0 && form.password.length < 12
  const needsPassword = !user && !form.password
  const isSelf = user?.id === me?.id

  return (
    <Modal
      open
      onClose={onClose}
      title={user ? `Edit ${user.fullName}` : 'Add user'}
      width={560}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.email.trim() || !form.fullName.trim() || needsPassword || passwordTooShort || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        {user ? (
          <Field label="Account" hint="A user cannot be moved between accounts; create them again where they belong.">
            <input className="field" value={user.tenantName ?? '—'} disabled />
          </Field>
        ) : (
          accountOptions.length > 1 && (
            <Field label="Account" required hint="Where this user signs in and what they see. Their role must fit that account.">
              <select
                className="field"
                value={form.tenantId}
                onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value, fleetScope: new Set() }))}
              >
                {accountOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.kind}
                  </option>
                ))}
              </select>
            </Field>
          )
        )}

        <div className="grid sm:grid-cols-2 gap-3.5">
          <Field label="Full name" required>
            <input
              className="field"
              value={form.fullName}
              onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              autoFocus
            />
          </Field>
          <Field label="Email" required>
            <input
              className="field"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              disabled={Boolean(user)}
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-3.5">
          <Field
            label="Role"
            required
            hint={isSelf ? 'You cannot change your own role' : 'Decides what they can see and do'}
          >
            <select
              className="field"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              disabled={isSelf}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Status" hint={isSelf ? 'You cannot disable yourself' : undefined}>
            <select
              className="field"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as typeof f.status }))
              }
              disabled={isSelf}
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </Field>
        </div>

        <Field label="Phone">
          <input
            className="field tnum"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </Field>

        <Field
          label={user ? 'Reset password' : 'Password'}
          required={!user}
          hint="At least 12 characters. Setting one signs them out everywhere."
          error={passwordTooShort ? 'Too short — 12 characters minimum' : undefined}
        >
          <input
            className="field"
            type="text"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder={user ? 'Leave blank to keep the current password' : ''}
            autoComplete="new-password"
          />
        </Field>

        {['fleet_manager', 'dispatcher', 'viewer', 'driver'].includes(form.role) && (
          <Field
            label="Restrict to fleets"
            hint="Leave empty for access to every fleet in the account"
          >
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {fleets.data?.items.map((fl) => {
                const on = form.fleetScope.has(fl.id)
                return (
                  <button
                    key={fl.id}
                    type="button"
                    className="chip"
                    onClick={() =>
                      setForm((f) => {
                        const next = new Set(f.fleetScope)
                        next.has(fl.id) ? next.delete(fl.id) : next.add(fl.id)
                        return { ...f, fleetScope: next }
                      })
                    }
                    style={{
                      color: on ? 'var(--accent-text)' : 'var(--color-ink-3)',
                      borderColor: on ? 'var(--accent)' : 'var(--color-rule)',
                      background: on ? 'var(--accent-dim)' : 'transparent',
                    }}
                  >
                    {fl.name}
                  </button>
                )
              })}
              {!fleets.data?.items.length && (
                <span className="text-[12px] text-[var(--color-ink-4)]">No fleets defined</span>
              )}
            </div>
          </Field>
        )}
      </div>
    </Modal>
  )
}

const KIND_ORDER: Record<Tenant['kind'], number> = { platform: 0, reseller: 1, client: 2 }

/** Which roles belong in which kind of account. */
const ROLES_BY_KIND: Record<Tenant['kind'], string[]> = {
  platform: ['platform_owner', 'platform_support'],
  reseller: ['reseller_admin', 'reseller_support'],
  client: ['client_admin', 'fleet_manager', 'dispatcher', 'viewer', 'driver'],
}

function tierOf(role: string): number {
  if (role.startsWith('platform')) return 1
  if (role.startsWith('reseller')) return 2
  if (role === 'client_admin') return 3
  return 4
}
